import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth, useTrip, WaitingIndicator, linkPresentationService } from '@trip-planner-monorepo/shared';
import { daysService } from '@trip-planner-monorepo/days';
import { accommodationService } from '@trip-planner-monorepo/accommodation';
import { placesService } from '@trip-planner-monorepo/places';
import { travelService } from '@trip-planner-monorepo/travel';
import { documentProcessingService } from '../services/documentProcessingService';
import DocumentUpload from './DocumentUpload';
import { FaChevronDown, FaChevronRight, FaCalendarAlt, FaHotel, FaPlane, FaMapMarkerAlt, FaFileAlt } from 'react-icons/fa';
import './UploadModal.css';

function UploadModal({ onClose, onProcessed, currentEntity = null }) {
  const [step, setStep] = useState('upload'); // 'upload' or 'association'
  const [file, setFile] = useState(null);
  const [selectedAssociations, setSelectedAssociations] = useState([]);
  const [entities, setEntities] = useState({
    days: [],
    accommodations: [],
    places: [],
    travel: []
  });
  const [loading, setLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [showAutoScrollLoader, setShowAutoScrollLoader] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    days: false,
    accommodations: false,
    places: false,
    travel: false
  });

  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();
  const { tripId } = useParams();

  /**
   * Fetches related entities for the current entity based on existing associations
   * @param {Object} currentEntity - The current entity to find relationships for
   * @returns {Array} Array of related entity objects
   */
  const fetchRelatedEntities = async (currentEntity) => {
    if (!currentEntity || !currentEntity.type || !currentEntity.id) {
      return [];
    }

    const relatedEntities = [];

    try {
      switch (currentEntity.type) {
        case 'accommodation': {
          // Get accommodation details to find day associations via contentBlocks
          const accommodation = await accommodationService.getAccommodationById(
            effectiveUserId,
            tripId,
            currentEntity.id
          );

          if (accommodation) {
            // Add ALL related days from contentBlocks (includes check-in, staying, and check-out)
            if (accommodation.contentBlocks && accommodation.contentBlocks.length > 0) {
              accommodation.contentBlocks.forEach(block => {
                if (block.dayId) {
                  relatedEntities.push({
                    entityType: 'day',
                    entityId: block.dayId,
                    entityName: `Day ${block.date ? new Date(block.date).getDate() : ''}`,
                    isRelated: true,
                    relationshipType: 'accommodation-day-contentblock'
                  });
                }
              });
            }

            // Add related place if accommodation is linked to a place
            if (accommodation.placeReference && accommodation.placeReference.placeId) {
              relatedEntities.push({
                entityType: 'place',
                entityId: accommodation.placeReference.placeId,
                entityName: accommodation.placeReference.placeName || 'Related Place',
                isRelated: true,
                relationshipType: 'accommodation-place'
              });
            }

            // Registry-aware (Issue 18): stays_on days and located_in place, for
            // registry-only trips where contentBlocks/placeReference above are empty.
            try {
              const { models } = await linkPresentationService.getRelatedEntityModels(
                effectiveUserId, tripId, 'accommodation', currentEntity.id, { surface: 'itinerary' }
              );
              (models || [])
                .filter(m => m.entityType === 'day')
                .forEach(m => relatedEntities.push({
                  entityType: 'day',
                  entityId: m.entityId,
                  entityName: m.entityName,
                  isRelated: true,
                  relationshipType: 'accommodation-day-registry'
                }));

              const { models: relatedModels } = await linkPresentationService.getRelatedEntityModels(
                effectiveUserId, tripId, 'accommodation', currentEntity.id, { surface: 'related' }
              );
              (relatedModels || [])
                .filter(m => m.entityType === 'place')
                .forEach(m => relatedEntities.push({
                  entityType: 'place',
                  entityId: m.entityId,
                  entityName: m.entityName,
                  isRelated: true,
                  relationshipType: 'accommodation-place-registry'
                }));
            } catch (error) {
              console.warn('UploadModal: could not read registry links for accommodation', error);
            }
          }
          break;
        }

        case 'day': {
          // For days, find accommodations and places associated with this day
          const allAccommodations = await accommodationService.fetchAccommodation(
            effectiveUserId,
            tripId
          );

          allAccommodations.forEach(accommodation => {
            if (accommodation.contentBlocks) {
              // Find ANY contentBlock that references this day (check-in, staying, or check-out)
              const dayBlock = accommodation.contentBlocks.find(
                block => block.dayId === currentEntity.id
              );
              if (dayBlock) {
                relatedEntities.push({
                  entityType: 'accommodation',
                  entityId: accommodation.id,
                  entityName: accommodation.summary || accommodation.name || 'Accommodation',
                  isRelated: true,
                  relationshipType: 'day-accommodation-contentblock'
                });

                // Also add the place if accommodation has one
                if (accommodation.placeReference && accommodation.placeReference.placeId) {
                  relatedEntities.push({
                    entityType: 'place',
                    entityId: accommodation.placeReference.placeId,
                    entityName: accommodation.placeReference.placeName || 'Related Place',
                    isRelated: true,
                    relationshipType: 'day-place-via-accommodation'
                  });
                }
              }
            }
          });

          // Registry-aware (Issue 18): stays_on accommodations for this day, for
          // registry-only trips where the contentBlocks scan above finds nothing.
          try {
            const { models } = await linkPresentationService.getRelatedEntityModels(
              effectiveUserId, tripId, 'day', currentEntity.id, { surface: 'itinerary' }
            );
            (models || [])
              .filter(m => m.entityType === 'accommodation')
              .forEach(m => relatedEntities.push({
                entityType: 'accommodation',
                entityId: m.entityId,
                entityName: m.entityName,
                isRelated: true,
                relationshipType: 'day-accommodation-registry'
              }));
          } catch (error) {
            console.warn('UploadModal: could not read registry links for day', error);
          }
          break;
        }

        case 'place': {
          // For places, find accommodations linked to this place
          // (accommodationService.getAccommodationsByPlace is registry-aware — Issue 6)
          const linkedAccommodations = await accommodationService.getAccommodationsByPlace(
            effectiveUserId,
            tripId,
            currentEntity.id
          );

          for (const accommodation of linkedAccommodations) {
            relatedEntities.push({
              entityType: 'accommodation',
              entityId: accommodation.id,
              entityName: accommodation.summary || accommodation.name || 'Accommodation',
              isRelated: true,
              relationshipType: 'place-accommodation'
            });

            // Add related days from the accommodation's contentBlocks
            if (accommodation.contentBlocks) {
              accommodation.contentBlocks.forEach(block => {
                if (block.dayId) {
                  relatedEntities.push({
                    entityType: 'day',
                    entityId: block.dayId,
                    entityName: `Day ${block.date ? new Date(block.date).getDate() : ''}`,
                    isRelated: true,
                    relationshipType: 'place-day-via-accommodation'
                  });
                }
              });
            }

            // Registry-aware (Issue 18): this hotel's stays_on days.
            try {
              const { models } = await linkPresentationService.getRelatedEntityModels(
                effectiveUserId, tripId, 'accommodation', accommodation.id, { surface: 'itinerary' }
              );
              (models || [])
                .filter(m => m.entityType === 'day')
                .forEach(m => relatedEntities.push({
                  entityType: 'day',
                  entityId: m.entityId,
                  entityName: m.entityName,
                  isRelated: true,
                  relationshipType: 'place-day-via-accommodation-registry'
                }));
            } catch (error) {
              console.warn('UploadModal: could not read registry stays for accommodation', accommodation.id, error);
            }
          }
          break;
        }

        case 'travel': {
          // Travel entities might be associated with specific days
          // This would need to be implemented based on travel data structure
          // For now, we'll leave this empty as travel relationships aren't clear from the current codebase
          break;
        }
      }

      // Remove duplicates
      const uniqueRelated = relatedEntities.filter((entity, index, arr) => {
        return arr.findIndex(e =>
          e.entityType === entity.entityType && e.entityId === entity.entityId
        ) === index;
      });

      return uniqueRelated;
    } catch (error) {
      console.error('Error fetching related entities:', error);
      return [];
    }
  };

  /**
   * Fetches all entities for the hierarchical association interface
   */
  const fetchEntities = async () => {
    if (!currentUser || !tripId) return;

    setLoading(true);
    try {
      const [days, accommodations, places, travel] = await Promise.all([
        daysService.fetchDays(effectiveUserId, tripId),
        accommodationService.fetchAccommodation(effectiveUserId, tripId),
        placesService.fetchPlaces(effectiveUserId, tripId),
        travelService.fetchTravel(effectiveUserId, tripId)
      ]);

      setEntities({
        days: days || [],
        accommodations: accommodations || [],
        places: places || [],
        travel: travel || []
      });
    } catch (error) {
      console.error('Error fetching entities:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Validates the selected associations for business logic consistency
   * @returns {Array} Array of validation error messages
   */
  const validateAssociations = () => {
    const errors = [];

    // If no associations selected, that's valid (document can exist without associations)
    if (selectedAssociations.length === 0) {
      return errors;
    }

    // Check for entity existence in fetched data
    selectedAssociations.forEach(association => {
      const { entityType, entityId, entityName } = association;

      switch (entityType) {
        case 'day':
          if (!entities.days.find(day => day.id === entityId)) {
            errors.push(`Selected day "${entityName}" no longer exists`);
          }
          break;
        case 'accommodation':
          if (!entities.accommodations.find(acc => acc.id === entityId)) {
            errors.push(`Selected accommodation "${entityName}" no longer exists`);
          }
          break;
        case 'place':
          if (!entities.places.find(place => place.id === entityId)) {
            errors.push(`Selected place "${entityName}" no longer exists`);
          }
          break;
        case 'travel':
          if (!entities.travel.find(travel => travel.id === entityId)) {
            errors.push(`Selected travel "${entityName}" no longer exists`);
          }
          break;
        default:
          errors.push(`Unknown entity type: ${entityType}`);
      }
    });

    // Check for duplicate associations
    const duplicates = selectedAssociations.filter((association, index, arr) => {
      return arr.findIndex(a =>
        a.entityType === association.entityType && a.entityId === association.entityId
      ) !== index;
    });

    if (duplicates.length > 0) {
      errors.push('Duplicate associations detected');
    }

    return errors;
  };

  /**
   * Handles file selection from DocumentUpload component
   */
  const handleFileSelected = (selectedFile) => {
    setFile(selectedFile);
    setValidationErrors([]); // Clear any previous validation errors
    setStep('association');
  };

  /**
   * Handles document upload completion
   */
  const handleDocumentProcessed = async (result) => {
    if (!file) return;

    // Validate associations before proceeding
    const errors = validateAssociations();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return; // Don't proceed with upload if validation fails
    }

    setLoading(true);
    setUploadError('');
    setValidationErrors([]);

    try {
      // Upload the document using documentProcessingService
      const uploadResult = await documentProcessingService.processDocument(
        file,
        effectiveUserId,
        tripId,
        'other' // Default document type, can be enhanced later
      );

      // Add associations to the uploaded document if any are selected
      if (selectedAssociations.length > 0) {
        const documentService = await import('../services/documentService');
        for (const association of selectedAssociations) {
          await documentService.documentService.addConnection(
            effectiveUserId,
            tripId,
            uploadResult.document.id,
            {
              entityType: association.entityType,
              entityId: association.entityId,
              entityName: association.entityName,
              connectionType: association.isPreSelected ? 'primary' : 'related',
              associatedAt: new Date().toISOString()
            }
          );
        }
      }

      if (onProcessed) {
        onProcessed({
          ...uploadResult,
          associations: selectedAssociations
        });
      }
      onClose();
    } catch (error) {
      console.error('Error uploading document:', error);
      setUploadError('Failed to upload document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggles section expansion
   */
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  /**
   * Handles entity selection/deselection
   */
  const toggleEntitySelection = (entityType, entityId, entityName) => {
    setSelectedAssociations(prev => {
      const existingIndex = prev.findIndex(
        item => item.entityType === entityType && item.entityId === entityId
      );

      if (existingIndex >= 0) {
        // Remove if already selected (but preserve pre-selected items with confirmation)
        const existing = prev[existingIndex];
        if (existing.isPreSelected) {
          // For pre-selected items, show a subtle warning but allow deselection
          console.log('Deselecting pre-selected entity:', entityName);
        }
        return prev.filter((_, index) => index !== existingIndex);
      } else {
        // Add if not selected
        return [...prev, {
          entityType,
          entityId,
          entityName,
          selectedAt: new Date().toISOString(),
          isPreSelected: false // Manually selected items are not pre-selected
        }];
      }
    });
  };

  /**
   * Checks if an entity is selected
   */
  const isEntitySelected = (entityType, entityId) => {
    return selectedAssociations.some(
      item => item.entityType === entityType && item.entityId === entityId
    );
  };

  /**
   * Formats date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  /**
   * Checks if an entity is pre-selected (from current context)
   */
  const isEntityPreSelected = (entityType, entityId) => {
    return selectedAssociations.some(
      item => item.entityType === entityType && item.entityId === entityId && item.isPreSelected
    );
  };

  /**
   * Gets the pre-selection type for an entity
   */
  const getPreSelectionType = (entityType, entityId) => {
    const association = selectedAssociations.find(
      item => item.entityType === entityType && item.entityId === entityId && item.isPreSelected
    );
    if (!association) return null;

    if (association.isRelated) {
      return 'related'; // Related through existing associations
    } else {
      return 'direct'; // Direct current page entity
    }
  };

  /**
   * Renders entity items for a section
   */
  const renderEntityItems = (entityType, entities, getDisplayName, getIcon) => {
    if (!entities || entities.length === 0) {
      return (
        <div className="entity-empty">
          <span>No {entityType} found</span>
        </div>
      );
    }

    return entities.map((entity, index) => {
      const displayName = getDisplayName(entity, index);
      const isSelected = isEntitySelected(entityType, entity.id);
      const isPreSelected = isEntityPreSelected(entityType, entity.id);
      const preSelectionType = getPreSelectionType(entityType, entity.id);

      let preSelectionClass = '';
      let preSelectionLabel = '';
      let tooltipText = '';

      if (preSelectionType === 'direct') {
        preSelectionClass = 'pre-selected-direct';
        preSelectionLabel = ' (Current page)';
        tooltipText = 'Pre-selected from current page';
      } else if (preSelectionType === 'related') {
        preSelectionClass = 'pre-selected-related';
        preSelectionLabel = ' (Related)';
        tooltipText = 'Pre-selected due to existing association';
      }

      return (
        <div
          key={entity.id}
          className={`entity-item ${isSelected ? 'selected' : ''} ${preSelectionClass}`}
          onClick={() => toggleEntitySelection(entityType, entity.id, displayName)}
          title={tooltipText || undefined}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => { }} // Handled by parent div click
            className="entity-checkbox"
          />
          <span className="entity-icon">{getIcon(entity)}</span>
          <span className="entity-name">
            {displayName}
            {preSelectionLabel && <span className="pre-selected-indicator">{preSelectionLabel}</span>}
          </span>
        </div>
      );
    });
  };

  /**
   * Adds a temporary highlight effect to an entity item
   * @param {Element} element - The DOM element to highlight
   */
  const addHighlightEffect = (element) => {
    // Add highlight class
    element.classList.add('scroll-highlight');

    // Remove highlight after animation
    setTimeout(() => {
      element.classList.remove('scroll-highlight');
    }, 2000);
  };

  /**
   * Scrolls to the first pre-selected entity to provide visual feedback
   * Enhanced debugging version to understand what's happening
   */
  const scrollToFirstPreselectedEntity = () => {
    // Show loading indicator
    setShowAutoScrollLoader(true);

    // Use multiple attempts with increasing delays to ensure DOM is ready
    let attempts = 0;
    const maxAttempts = 8;

    const tryScroll = () => {
      attempts++;

      const allEntityItems = document.querySelectorAll('.entity-item');

      // Look for checked checkboxes first (most reliable)
      const checkedItems = document.querySelectorAll('.entity-item .entity-checkbox:checked');

      // Only log on first and last attempts to reduce noise
      if (attempts === 1 || attempts === maxAttempts) {
        console.log(`[AutoScroll] Attempt ${attempts}: ${allEntityItems.length} entities, ${checkedItems.length} checked`);
      }

      if (checkedItems.length > 0) {
        // Get the parent entity-item of the first checked checkbox
        const targetElement = checkedItems[0].closest('.entity-item');

        if (targetElement) {
          console.log('[AutoScroll] Successfully found and scrolling to pre-selected entity');

          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });

          // Add temporary highlight effect
          addHighlightEffect(targetElement);

          // Hide loading indicator
          setShowAutoScrollLoader(false);
          return true; // Success
        }
      }

      // Fallback: Look for pre-selected classes
      const preselectedElements = document.querySelectorAll(
        '.entity-item.pre-selected-direct, .entity-item.pre-selected-related, .entity-item.selected'
      );

      if (preselectedElements.length > 0) {
        const targetElement = preselectedElements[0];
        console.log('[AutoScroll] Fallback: scrolling to pre-selected element');

        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        addHighlightEffect(targetElement);

        // Hide loading indicator
        setShowAutoScrollLoader(false);
        return true;
      }

      // If no elements found and we haven't reached max attempts, try again
      if (attempts < maxAttempts) {
        const delay = attempts * 300; // Longer delays
        setTimeout(tryScroll, delay);
      } else {
        console.log('[AutoScroll] Could not find pre-selected entities after all attempts');
        // Hide loading indicator even if we failed
        setShowAutoScrollLoader(false);
      }

      return false;
    };

    // Start the scroll attempts
    tryScroll();
  };

  // Fetch entities when component mounts
  useEffect(() => {
    fetchEntities();
  }, [currentUser, tripId]);

  // Pre-select current entity and related entities
  useEffect(() => {
    const setupPreselection = async () => {
      if (currentEntity && currentEntity.type && currentEntity.id) {
        const preselectedAssociations = [];

        // Always pre-select the current entity
        preselectedAssociations.push({
          entityType: currentEntity.type,
          entityId: currentEntity.id,
          entityName: currentEntity.name,
          selectedAt: new Date().toISOString(),
          isPreSelected: true, // Direct pre-selection
          isRelated: false
        });

        // Fetch and pre-select related entities
        try {
          const relatedEntities = await fetchRelatedEntities(currentEntity);
          relatedEntities.forEach(relatedEntity => {
            preselectedAssociations.push({
              entityType: relatedEntity.entityType,
              entityId: relatedEntity.entityId,
              entityName: relatedEntity.entityName,
              selectedAt: new Date().toISOString(),
              isPreSelected: true, // Related pre-selection
              isRelated: true,
              relationshipType: relatedEntity.relationshipType
            });
          });
        } catch (error) {
          console.error('Error fetching related entities for pre-selection:', error);
        }

        setSelectedAssociations(preselectedAssociations);

        // Auto-expand relevant sections
        const sectionMap = {
          'day': 'days',
          'accommodation': 'accommodations',
          'place': 'places',
          'travel': 'travel'
        };

        const sectionsToExpand = {};
        preselectedAssociations.forEach(association => {
          const section = sectionMap[association.entityType];
          if (section) {
            sectionsToExpand[section] = true;
          }
        });

        setExpandedSections(prev => ({
          ...prev,
          ...sectionsToExpand
        }));

        // Auto-scroll to first pre-selected entity after a short delay for expansion animation
        if (preselectedAssociations.length > 0) {
          setTimeout(() => {
            scrollToFirstPreselectedEntity();
          }, 500); // Allow more time for section expansion and entity rendering
        }
      }
    };

    setupPreselection();
  }, [currentEntity, currentUser, tripId]);

  return (
    <div className="upload-modal-overlay">
      <div className="upload-modal-dialog upload-modal-container">
        <button className="upload-modal-close" onClick={onClose} title="Close">&times;</button>

        {step === 'upload' ? (
          <>
            <div className="upload-modal-header">
              <h2>Upload Document</h2>
            </div>
            <DocumentUpload onProcessed={handleFileSelected} onFileSelected={handleFileSelected} />
          </>
        ) : (
          <>
            <div className="upload-modal-header">
              <h2>Associate Document</h2>
            </div>
            <div className="association-container">
              <div className="association-content">
                <div className="association-instructions">
                  <p>Select the items you'd like to associate with this document:</p>
                </div>

                <div className="file-info modal-file-info">
                  <FaFileAlt className="file-icon modal-file-icon" />
                  <span className="file-name modal-file-name">{file?.name}</span>
                </div>

                {uploadError && (
                  <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
                    {uploadError}
                  </div>
                )}

                {validationErrors.length > 0 && (
                  <div className="validation-errors" style={{ marginBottom: '1rem' }}>
                    <div style={{ color: '#d32f2f', fontWeight: '600', marginBottom: '0.5rem' }}>
                      Please fix the following issues:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#d32f2f' }}>
                      {validationErrors.map((error, index) => (
                        <li key={index} style={{ marginBottom: '0.25rem' }}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {loading ? (
                  <div style={{ padding: '20px 0' }}>
                    <WaitingIndicator size="small" message="Please wait while the document is being analysed" />
                  </div>
                ) : (
                  <div className="association-wrapper">
                    {showAutoScrollLoader && (
                      <div className="auto-scroll-loading-overlay">
                        <div className="auto-scroll-loading">
                          <div className="auto-scroll-spinner"></div>
                          <span>Finding your pre-selected items...</span>
                        </div>
                      </div>
                    )}
                    <div className="hierarchical-association">
                      {/* Days Section */}
                      <div className="association-section">
                        <div
                          className="section-header"
                          onClick={() => toggleSection('days')}
                        >
                          <span className="expand-icon">
                            {expandedSections.days ? <FaChevronDown /> : <FaChevronRight />}
                          </span>
                          <FaCalendarAlt className="section-icon" />
                          <span className="section-title">Days ({entities.days.length})</span>
                        </div>

                        {expandedSections.days && (
                          <div className="section-content">
                            {renderEntityItems(
                              'day',
                              entities.days,
                              (day, index) => `Day ${index + 1} - ${formatDate(day.date)}`,
                              () => <FaCalendarAlt />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Accommodations Section */}
                      <div className="association-section">
                        <div
                          className="section-header"
                          onClick={() => toggleSection('accommodations')}
                        >
                          <span className="expand-icon">
                            {expandedSections.accommodations ? <FaChevronDown /> : <FaChevronRight />}
                          </span>
                          <FaHotel className="section-icon" />
                          <span className="section-title">Accommodations ({entities.accommodations.length})</span>
                        </div>

                        {expandedSections.accommodations && (
                          <div className="section-content">
                            {renderEntityItems(
                              'accommodation',
                              entities.accommodations,
                              (acc, index) => `${acc.summary || acc.details?.name} - ${acc.details?.city || ''}`,
                              () => <FaHotel />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Places Section */}
                      <div className="association-section">
                        <div
                          className="section-header"
                          onClick={() => toggleSection('places')}
                        >
                          <span className="expand-icon">
                            {expandedSections.places ? <FaChevronDown /> : <FaChevronRight />}
                          </span>
                          <FaMapMarkerAlt className="section-icon" />
                          <span className="section-title">Places ({entities.places.length})</span>
                        </div>

                        {expandedSections.places && (
                          <div className="section-content">
                            {renderEntityItems(
                              'place',
                              entities.places,
                              (place, index) => place.name,
                              () => <FaMapMarkerAlt />
                            )}
                          </div>
                        )}
                      </div>

                      {/* Travel Section */}
                      <div className="association-section">
                        <div
                          className="section-header"
                          onClick={() => toggleSection('travel')}
                        >
                          <span className="expand-icon">
                            {expandedSections.travel ? <FaChevronDown /> : <FaChevronRight />}
                          </span>
                          <FaPlane className="section-icon" />
                          <span className="section-title">Travel ({entities.travel.length})</span>
                        </div>

                        {expandedSections.travel && (
                          <div className="section-content">
                            {renderEntityItems(
                              'travel',
                              entities.travel,
                              (travel, index) => `${travel.summary || travel.details?.type} - ${travel.details?.from || ''} to ${travel.details?.to || ''}`,
                              () => <FaPlane />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="association-actions">
                <button
                  className="btn-secondary"
                  onClick={() => setStep('upload')}
                >
                  Back to Upload
                </button>
                <button
                  className="btn-primary"
                  onClick={handleDocumentProcessed}
                  disabled={!file || loading || validationErrors.length > 0}
                  title={validationErrors.length > 0 ? 'Please fix validation errors before uploading' : undefined}
                >
                  {loading ? 'Uploading...' : 'Upload & Associate'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default UploadModal; 